import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { useKeyModificationDialogStore } from 'components/WorkspaceEditor/components/KeyModificationWarningDialog'
import { DesignationField } from 'components/WorkspaceEditor/features/drawingDesignation'
import { shouldHideElement } from 'components/WorkspaceEditor/features/drawingDesignation/utils/elementVisibility'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { ADDITIONAL_ELEMENT_SUFFIX } from 'components/WorkspaceEditor/utils'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { preserveTrailingSpaceWithoutNbsp } from '../../ContentEditor/contentUtils'
import { EmptyPlainTextContainer } from '../EmptyPlainTextContainer'
import { EditorContent } from './EditorContent'
import { KeyedText } from './KeyedText'
import { usePlainTextController } from './hooks/usePlainTextController'
import { usePlainTextStyles } from './usePlainTextStyles'

// Utility functions
const generateSuggestionDataAttribute = (suggestionId?: string) => {
    return suggestionId ? `content-suggestion-id-${suggestionId}` : undefined
}

/** Props for the PlainText component. */
export interface PlainTextProps {
    /** The contentItem object that holds the text property that needs to render */
    contentItem: Partial<TextContentElement>
    /** The parent node (for Product Type detection) */
    node?: DataNode
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** The classes to apply the styling */
    classes?: string
}

/**
 * An editable component that renders plain text. It uses Typography (caption variant) from Material-UI as a placeholder for CKEditor (ContentEditor).
 * Can be editable based on:
 * - by default, the component is not editable
 * - when the component has changes (initial suggestions)
 * - when focused or clicked
 * @param {PlainTextProps} props - Props for the component.
 */
export const PlainText = memo<PlainTextProps>(
    ({ contentItem, node, inactive = false, editable = false, classes }) => {
        /** controller props and handlers */
        const {
            text,
            keyedText,
            keyedTextValue,
            formattedContentId,
            initialData,
            initialSuggestions,
            clickCoordinates,
            ckeToggle,
            shouldRenderContentEditor,
            isKeyedText,
            handlers,
        } = usePlainTextController(contentItem, node)

        const plainTextStyles = usePlainTextStyles()
        const containerRef = useRef<HTMLSpanElement>(null)
        const { previewFeatures } = usePreviewFeatures()

        // Prevent CKEditor from trimming trailing spaces
        const cleanedInitialData = useMemo(
            () => preserveTrailingSpaceWithoutNbsp(initialData),
            [initialData]
        )
        const isEditMode = shouldRenderContentEditor && !inactive && editable
        const showEmptyPlainTextContainer = isKeyedText && !keyedTextValue && !inactive
        const displayText = text ?? ''
        const hasEmptyText = !text?.trim()

        // Check if this is the first content item in a PT (Product Type name/key portion)
        // PT names can be split across multiple content items where the first item
        // is the key (e.g., "Cementitious Patching Mortar") without a colon
        const isPTFirstItem = Boolean(
            node?.isProductType &&
            !node?.isUserAdded &&
            contentItem.id === node?.contentItemNodes?.[0]?.id
        )

        // Only the FIRST element should render the DesignationField to avoid duplication
        const isEditDrawingDesignationMode =
            previewFeatures.hasDrawingDesignation &&
            node?.isProductType &&
            isKeyedText &&
            contentItem.id &&
            node?.key &&
            isPTFirstItem  // Only first element renders designation


        // Determine if this is a PT key (Product Type key that should not be editable)
        // This includes: keyed text in PT nodes OR the first content item in a PT
        const isPTKey = Boolean(
            node?.isProductType &&
            !node?.isUserAdded &&
            (isKeyedText || isPTFirstItem)
        )

        // Lock key editing if it's a PT key OR if feature flag is disabled for keyed text
        const shouldLockKey = isPTKey || (isKeyedText && !previewFeatures.hasKeyModification)

        // Memoize container class to prevent recreation on each render
        const containerClass = useMemo(() => {
            if (!editable) return plainTextStyles.plainTextContainerReadOnly
            if (hasEmptyText) return plainTextStyles.plainTextContainer
            return plainTextStyles.plainTextContainerWithContent
        }, [editable, hasEmptyText, plainTextStyles])

        // Generate suggestion data attribute
        const suggestionDataAttribute = useMemo(
            () => generateSuggestionDataAttribute(initialSuggestions?.[0]?.id),
            [initialSuggestions]
        )

        // Focus handler that blocks activation for locked keys when not in edit mode
        const handleFocusCapture = useCallback(
            (event?: React.FocusEvent<HTMLElement>) => {
                if (isEditDrawingDesignationMode) {
                    handlers.onFocusCapture(event)
                    return
                }

                // For locked keys not in edit mode, don't activate editor on focus
                // Let the onClick handler determine if we should activate based on where they clicked
                if (shouldLockKey && !isEditMode) {
                    return
                }

                // For all other cases, activate the editor
                handlers.onFocusCapture(event)
            },
            [shouldLockKey, isEditMode, isEditDrawingDesignationMode, handlers]
        )

        // Click handler that checks if clicking on locked key portion
        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLElement>) => {
                if (isEditDrawingDesignationMode) {
                    handlers.onClick(event)
                    return
                }

                // For PT first items (non-keyed), the entire content is the key - block entirely
                if (isPTFirstItem && !isKeyedText) {
                    event.preventDefault()
                    event.stopPropagation()
                    // Show PT key warning dialog if feature flag is enabled and content is not inactive
                    if (previewFeatures.hasKeyModification && !inactive) {
                        const { showDialog, closeDialog } = useKeyModificationDialogStore.getState()
                        showDialog({
                            isPTKey: true,
                            originalKey: text || '',
                            modifiedKey: text || '',
                            onProceed: closeDialog,
                            onCancel: closeDialog,
                        })
                    }
                    return
                }

                // Check if click is on KeyedText (locked key portion)
                const target = event.target as HTMLElement
                const clickedOnKeyedText = target.closest('[data-testid^="keyed-text"]')

                if (shouldLockKey && clickedOnKeyedText) {
                    // Don't activate editor when clicking on locked key portion
                    // Let the event propagate to KeyedText so it can show the dialog (if enabled)
                    return
                }

                // For locked keys, if clicking on value portion, manually activate editor
                if (shouldLockKey && !clickedOnKeyedText) {
                    // Manually call focus handler since we blocked it earlier
                    handlers.onFocusCapture()
                }

                // For all cases (locked key value or unlocked keys), call onClick
                handlers.onClick(event)
            },
            [shouldLockKey, isPTFirstItem, isKeyedText, text, inactive, handlers, previewFeatures.hasKeyModification, isEditDrawingDesignationMode]
        )

        // Check if element should be hidden (Drawing Designation feature)
        if (shouldHideElement({
            hasDrawingDesignation: previewFeatures.hasDrawingDesignation,
            node,
            contentItem,
            isPTFirstItem,
            isEditMode
        })) {
            return null
        }

        return (
            <Typography
                variant="caption"
                key={formattedContentId}
                data-testid={formattedContentId}
                data-suggestion={suggestionDataAttribute}
                className={clsx(classes, containerClass)}
                tabIndex={0}
                ref={ckeToggle.active ? ckeToggle.placeholderRef : containerRef}
                onKeyDown={handlers.onKeyDown}
                onKeyUp={handlers.onKeyUp}
                onFocusCapture={handleFocusCapture}
                onClick={handleClick}
                onMouseMove={handlers.onMouseMove}
            >
                {isEditMode ? (
                    <EditorContent
                        initialData={cleanedInitialData}
                        initialSuggestions={initialSuggestions}
                        clickCoordinates={clickCoordinates}
                        handlers={handlers}
                        isKeyedText={isKeyedText}
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        classes={classes}
                        designationProps={{
                            isProductType: node?.isProductType,
                            contentItemId: contentItem.id,
                            nodeId: node?.key,
                            isPTKey,
                        }}
                    />
                ) : isEditDrawingDesignationMode ? (
                    <DesignationField
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        contentItemId={contentItem?.id || ''}
                        editable={editable}
                        inactive={inactive}
                    />
                ) : shouldLockKey && isKeyedText ? (
                    // For locked keys not in edit mode, render key and value separately
                    // so we can block clicks on the key portion
                    <>
                        <KeyedText
                            keyedText={keyedText}
                            classes={classes}
                            data-testid="keyed-text-key-display"
                            isPTKey={isPTKey}
                        />
                        {keyedTextValue}
                    </>
                ) : (
                    displayText
                )}
                {showEmptyPlainTextContainer && (
                    <EmptyPlainTextContainer
                        element={{
                            id: `${contentItem.id}${ADDITIONAL_ELEMENT_SUFFIX}`,
                            text: '',
                            type: contentItem.type,
                        }}
                        inactive={inactive}
                        editable={true}
                    />
                )}
            </Typography>
        )
    }
)
